import { useEffect, useState } from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { CheckCircle2, XCircle, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  darDeBajaAgenciaDesdeEmail,
  solicitarPublicarMasDesdeEmail,
} from "@/lib/email-gestion.functions";

const searchSchema = z.object({
  action: z.enum(["baja", "alta"]).optional(),
  aid: z.string().uuid().optional(),
  t: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_term: z.string().optional(),
  utm_content: z.string().optional(),
});

export const Route = createFileRoute("/email-gestion")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Gestión de suscripción — Bizaclia" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: EmailGestionPage,
});

function EmailGestionPage() {
  const search = useSearch({ from: "/email-gestion" });
  const { action, aid } = search;

  if (!aid || !action) {
    return (
      <Shell>
        <p className="text-muted-foreground">Enlace no válido.</p>
      </Shell>
    );
  }

  if (action === "baja") return <BajaFlow aid={aid} token={search.t ?? null} />;
  return <AltaFlow search={search} aid={aid} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto max-w-xl px-4 py-16">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Ir a Bizaclia
      </Link>
      <div className="rounded-2xl border border-border bg-background p-8 shadow-sm">
        {children}
      </div>
    </main>
  );
}

function BajaFlow({ aid, token }: { aid: string; token: string | null }) {
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [nombre, setNombre] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await darDeBajaAgenciaDesdeEmail({ data: { agencia_id: aid, token } });
        if (cancelled) return;
        setNombre(res.nombre);
        setState("ok");
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Error");
        setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [aid, token]);

  return (
    <Shell>
      {state === "loading" && (
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Procesando tu baja…
        </div>
      )}
      {state === "ok" && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-semibold">Baja confirmada</h1>
          </div>
          <p className="text-muted-foreground">
            {nombre ? <><strong>{nombre}</strong> </> : "La agencia "}ha sido dada de baja.
            No volveremos a enviarte comunicaciones.
          </p>
          <p className="text-sm text-muted-foreground">
            Si ha sido un error, escríbenos a{" "}
            <a href="mailto:hola@bizaclia.com" className="text-primary underline">
              hola@bizaclia.com
            </a>
            .
          </p>
        </div>
      )}
      {state === "error" && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <XCircle className="h-8 w-8 text-destructive" />
            <h1 className="text-2xl font-semibold">No se pudo procesar</h1>
          </div>
          <p className="text-muted-foreground">{error}</p>
        </div>
      )}
    </Shell>
  );
}

function AltaFlow({
  aid,
  search,
}: {
  aid: string;
  search: z.infer<typeof searchSchema>;
}) {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  

  const [state, setState] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState("sending");
    setError(null);
    try {
      await solicitarPublicarMasDesdeEmail({
        data: {
          agencia_id: aid,
          nombre: nombre.trim(),
          email: email.trim(),
          telefono: telefono.trim() || null,

          utm_source: search.utm_source ?? null,
          utm_medium: search.utm_medium ?? null,
          utm_campaign: search.utm_campaign ?? null,
          utm_term: search.utm_term ?? null,
          utm_content: search.utm_content ?? null,
          utm_landing_page:
            typeof window !== "undefined" ? window.location.href.slice(0, 2000) : null,
          utm_referrer:
            typeof document !== "undefined"
              ? document.referrer.slice(0, 2000) || null
              : null,
        },
      });
      setState("ok");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
      setState("error");
    }
  };

  if (state === "ok") {
    return (
      <Shell>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-semibold">¡Gracias!</h1>
          </div>
          <p className="text-muted-foreground">
            Hemos recibido tu solicitud. Nos pondremos en contacto contigo muy pronto
            para publicar más anuncios gratis.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-2 mb-6">
        <h1 className="text-2xl font-semibold">Publicar más anuncios gratis</h1>
        <p className="text-sm text-muted-foreground">
          Déjanos tus datos y nos pondremos en contacto contigo para publicar más
          anuncios de tu agencia en Bizaclia, sin coste.
        </p>
      </div>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="nombre">Nombre *</Label>
          <Input
            id="nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            required
            maxLength={120}
            autoComplete="name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Correo electrónico *</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            maxLength={255}
            autoComplete="email"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="telefono">Teléfono</Label>
          <Input
            id="telefono"
            type="tel"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            maxLength={40}
            autoComplete="tel"
            placeholder="Ej. 600 123 456"
          />
        </div>

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
        <Button type="submit" disabled={state === "sending"} className="w-full">
          {state === "sending" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Enviando…
            </>
          ) : (
            "Enviar solicitud"
          )}
        </Button>
      </form>
    </Shell>
  );
}
